import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { ChartLine, Warning, Broadcast, Lightning, TrendUp } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("en-AE").format(n);
const aed = (n) => `AED ${fmt(n)}`;

// Curated gradient palette so each KPI tile has a distinct accent
const GRADIENTS = [
  "from-[#0020C2] to-[#3B5BFF]",
  "from-emerald-500 to-emerald-400",
  "from-amber-500 to-amber-300",
  "from-violet-500 to-fuchsia-400",
  "from-rose-500 to-rose-400",
  "from-cyan-500 to-sky-400",
];

const KPI = ({ label, value, delta, idx = 0, icon: Icon }) => (
  <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-5 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all">
    <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${GRADIENTS[idx % GRADIENTS.length]} opacity-80`} />
    <div className="flex items-center justify-between">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">{label}</div>
      {Icon && (
        <div className="p-1.5 rounded-lg bg-gray-50 text-gray-400 group-hover:scale-110 transition-transform">
          <Icon size={14} weight="bold" />
        </div>
      )}
    </div>
    <div className="mt-2 text-2xl font-black font-mono tracking-tight text-gray-900">{value}</div>
    {delta && <div className={`text-xs mt-1 font-mono ${delta.startsWith("−") || delta.startsWith("-") ? "text-emerald-700" : "text-[#0020C2]"}`}>{delta}</div>}
  </div>
);

const SectionCard = ({ children, ...rest }) => (
  <div {...rest} className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
    {children}
  </div>
);

const Callout = ({ tone = "amber", icon: Icon, children }) => {
  const styles = {
    amber: "border-amber-200 bg-gradient-to-r from-amber-50 to-amber-100/50 text-amber-900",
    blue:  "border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 text-[#0020C2]",
    purple: "border-purple-200 bg-gradient-to-r from-purple-50 to-fuchsia-50 text-purple-900",
  };
  return (
    <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-xs ${styles[tone]}`}>
      {Icon && <Icon size={16} weight="fill" className="shrink-0 mt-0.5" />}
      <div className="flex-1">{children}</div>
    </div>
  );
};

const Badge = ({ tone, children }) => {
  const tones = {
    high:    "border-red-300 bg-red-50 text-[#DC2626]",
    medium:  "border-amber-300 bg-amber-50 text-amber-700",
    low:     "border-gray-300 bg-gray-50 text-gray-700",
    healthy: "border-emerald-300 bg-emerald-50 text-emerald-700",
    watch:   "border-amber-300 bg-amber-50 text-amber-700",
    breach:  "border-red-300 bg-red-50 text-[#DC2626]",
  };
  return <span className={`inline-block text-[10px] font-bold tracking-wider uppercase border rounded-md px-2 py-0.5 ${tones[tone] || tones.low}`}>{children}</span>;
};

const SectionHeader = ({ eyebrow, title, sub, right }) => (
  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white flex items-start justify-between">
    <div>
      {eyebrow && <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">{eyebrow}</div>}
      <h3 className="text-xl font-black tracking-tight mt-0.5">{title}</h3>
      {sub && <div className="text-[11px] font-mono text-gray-500 mt-1">{sub}</div>}
    </div>
    {right}
  </div>
);

const EmptyState = ({ icon: Icon, title, body }) => (
  <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
    <div className="mb-3 p-3 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 ring-1 ring-blue-100">
      <Icon size={24} weight="duotone" className="text-[#0020C2]" />
    </div>
    <div className="text-sm font-semibold text-gray-900">{title}</div>
    {body && <div className="text-xs text-gray-500 mt-1 max-w-sm">{body}</div>}
  </div>
);

function Page({ title, eyebrow, subtitle, endpoint, render, testid }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get(endpoint).then(({ data }) => setData(data)).catch(() => setErr(true)).finally(() => setLoading(false));
  }, [endpoint]);
  return (
    <div>
      <PageHeader eyebrow={eyebrow} title={title} subtitle={subtitle} testid={testid} />
      <div className="p-8">
        {loading ? <EmptyState icon={Lightning} title="Loading…" body="Pulling live telecom KPIs from your tenant." /> :
         err ? <EmptyState icon={Warning} title="Couldn't load data" body="Please refresh or check your tenant's connector." /> :
         data ? render(data) :
                <EmptyState icon={Warning} title="No data yet" body="No records returned for this view." />}
      </div>
    </div>
  );
}

export function FinanceBssPage() {
  return <Page testid="telecom-finance-bss-title"
    eyebrow="Telecom Solution" title="Finance × BSS Insights"
    subtitle="One pane that joins Oracle Fusion GL with your BSS revenue ledger. Ask finance questions across both."
    endpoint="/telecom/finance-bss/kpis"
    render={(d) => (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {d.kpis.map((k, i) => <KPI key={k.label} idx={i} icon={ChartLine} {...k} />)}
        </div>
        <SectionCard data-testid="telecom-finance-bss-table">
          <SectionHeader
            eyebrow="Segment Join Breakdown"
            title="Fusion GL vs BSS Revenue"
            sub={`Sources: ${d.sources_connected.join(" · ")}`}
          />
          <table className="w-full text-sm">
            <thead className="bg-gray-50/60"><tr className="text-left">
              {["Segment", "Fusion GL (AED)", "BSS Ledger (AED)", "Gap %"].map((h) =>
                <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-200">{h}</th>)}
            </tr></thead>
            <tbody>
              {d.join_breakdown.map((r) => (
                <tr key={r.segment} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium">{r.segment}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmt(r.fusion)}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmt(r.bss)}</td>
                  <td className={`px-4 py-3 font-mono text-right font-bold ${Math.abs(r.gap_pct) > 2 ? "text-[#DC2626]" : "text-emerald-700"}`}>
                    {r.gap_pct > 0 ? "+" : ""}{r.gap_pct.toFixed(2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
        <Callout tone={d.source === "live" ? "blue" : "amber"} icon={d.source === "live" ? Lightning : Warning}>
          {d.source === "live" ? (
            <><strong>Live tenant data.</strong> Last updated {(d.as_of || "").slice(0, 19).replace("T", " ")} UTC. Edit via the Database Explorer or push to <code className="text-[10px] font-mono">PUT /api/telecom/finance_bss</code>.</>
          ) : (
            <><strong>Seeded baseline data.</strong> Connect your BSS revenue feed (Amdocs / Comverse / Optiva) via <code className="text-[10px] font-mono">PUT /api/telecom/finance_bss</code> to replace with live numbers.</>
          )}
        </Callout>
      </div>
    )} />;
}

export function RevenueAssurancePage() {
  return <Page testid="telecom-ra-title"
    eyebrow="Telecom Solution" title="Revenue Assurance AI"
    subtitle="Automatically detects billing vs network-usage gaps and flags potential leakage in real time."
    endpoint="/telecom/revenue-assurance/alerts"
    render={(d) => (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPI idx={0} icon={Warning}   label="Total Leakage (Apr-26)" value={aed(d.summary.total_leakage_aed)} />
          <KPI idx={1} icon={Lightning} label="Open Alerts"             value={d.summary.alerts_open} />
          <KPI idx={2} icon={ChartLine} label="Resolved (last 90d)"     value={d.summary.alerts_resolved} />
          <KPI idx={3} icon={TrendUp}   label="Est. Monthly Savings"    value={d.summary.estimated_monthly_savings} />
        </div>
        <SectionCard data-testid="telecom-ra-table">
          <SectionHeader eyebrow="AI Detection" title="Open Leakage Alerts" />
          <table className="w-full text-sm">
            <thead className="bg-gray-50/60"><tr className="text-left">
              {["Alert ID", "Rule", "Amount", "Subscribers", "Severity", "Opened"].map((h) =>
                <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-200">{h}</th>)}
            </tr></thead>
            <tbody>
              {d.alerts.map((a) => (
                <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs">{a.id}</td>
                  <td className="px-4 py-3 font-medium">{a.rule}</td>
                  <td className="px-4 py-3 font-mono text-right">{aed(a.amount_aed)}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmt(a.subscribers)}</td>
                  <td className="px-4 py-3"><Badge tone={a.severity}>{a.severity}</Badge></td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{a.opened_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      </div>
    )} />;
}

export function SpectrumPage() {
  return <Page testid="telecom-spectrum-title"
    eyebrow="Telecom Solution" title="Spectrum & Licence Management"
    subtitle="Inventory of every spectrum licence the operator holds, with renewal countdown and utilisation tracking."
    endpoint="/telecom/spectrum/licences"
    render={(d) => (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPI idx={0} icon={Broadcast} label="Total Licences"        value={d.summary.total_licences} />
          <KPI idx={1} icon={Warning}   label="Expiring in 180 days"  value={d.summary.expiring_180d} />
          <KPI idx={2} icon={ChartLine} label="Annual Fee"            value={aed(d.summary.annual_fee_aed)} />
          <KPI idx={3} icon={TrendUp}   label="Avg Utilisation"       value={`${d.summary.avg_utilisation_pct}%`} />
        </div>
        <SectionCard data-testid="telecom-spectrum-table">
          <SectionHeader eyebrow="Spectrum Inventory" title="Active Licences" />
          <table className="w-full text-sm">
            <thead className="bg-gray-50/60"><tr className="text-left">
              {["Code", "Band", "Tech", "Region", "Start", "End", "Annual Fee", "Utilisation"].map((h) =>
                <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-200">{h}</th>)}
            </tr></thead>
            <tbody>
              {d.licences.map((l) => {
                const today = new Date(); const end = new Date(l.end);
                const daysLeft = Math.floor((end - today) / 86400000);
                const expSoon = daysLeft < 365;
                return (
                  <tr key={l.code} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs font-bold">{l.code}</td>
                    <td className="px-4 py-3 font-mono text-xs">{l.band}</td>
                    <td className="px-4 py-3 text-xs">{l.tech}</td>
                    <td className="px-4 py-3 text-xs">{l.region}</td>
                    <td className="px-4 py-3 font-mono text-xs">{l.start}</td>
                    <td className={`px-4 py-3 font-mono text-xs ${expSoon ? "text-[#DC2626] font-bold" : ""}`}>{l.end}</td>
                    <td className="px-4 py-3 font-mono text-right">{fmt(l.annual_fee_aed)}</td>
                    <td className="px-4 py-3 font-mono text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-[#0020C2] to-[#3B5BFF] rounded-full" style={{ width: `${l.utilisation_pct}%` }} />
                        </div>
                        <span className="text-xs font-mono w-9 text-right">{l.utilisation_pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </SectionCard>
        <Callout tone="blue" icon={Lightning}>
          <strong>Unique to Vaku.</strong> No other ERP/BSS platform tracks spectrum licences as first-class assets. Renewal alerts hook into the existing automations engine.
        </Callout>
      </div>
    )} />;
}

export function SlicingPage() {
  return <Page testid="telecom-slicing-title"
    eyebrow="Telecom Solution" title="5G Network Slicing · Billing Intelligence"
    subtitle="Per-slice revenue, SLA targets vs actuals, and breach-credit exposure across every active 5G network slice."
    endpoint="/telecom/slicing/billing"
    render={(d) => (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPI idx={0} icon={Broadcast} label="Active Slices"          value={d.summary.active_slices} />
          <KPI idx={1} icon={ChartLine} label="Monthly Slice Revenue"  value={aed(d.summary.monthly_slice_revenue)} />
          <KPI idx={2} icon={Warning}   label="SLA Breach Credits"     value={aed(d.summary.sla_breach_credits)} />
          <KPI idx={3} icon={TrendUp}   label="Avg Utilisation"        value={`${d.summary.avg_slice_utilisation}%`} />
        </div>
        <SectionCard data-testid="telecom-slicing-table">
          <SectionHeader eyebrow="5G Slice Billing" title="Active Slices · Revenue & SLA" />
          <table className="w-full text-sm">
            <thead className="bg-gray-50/60"><tr className="text-left">
              {["Slice", "Type", "Tenant", "Subs", "Revenue (AED)", "SLA Target", "SLA Actual", "Status"].map((h) =>
                <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-200">{h}</th>)}
            </tr></thead>
            <tbody>
              {d.slices.map((s) => (
                <tr key={s.slice_id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs font-bold">{s.slice_id}</td>
                  <td className="px-4 py-3 font-mono text-xs">{s.type}</td>
                  <td className="px-4 py-3 font-medium">{s.tenant}</td>
                  <td className="px-4 py-3 font-mono text-right text-xs">{fmt(s.subscribers)}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmt(s.revenue_aed)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{s.sla_target}</td>
                  <td className="px-4 py-3 font-mono text-xs">{s.sla_actual}</td>
                  <td className="px-4 py-3"><Badge tone={s.status}>{s.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
        <Callout tone="purple" icon={Lightning}>
          <strong>3-year competitive advantage.</strong> Slice-aware billing is required for 5G SA commercialisation — Vaku is the only ERP+BSS layer that handles eMBB / URLLC / mMTC pricing natively today.
        </Callout>
      </div>
    )} />;
}
